import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import {
	BookingStateEnum,
	CancellationReasonDispatcherBookingEnum,
	PassengerBookingView,
	PaymentStateEnum,
} from '@mopla/data-models';

import { Message } from '../../../molecules/Message/Message';

interface IProps {
	bookedItinerary: PassengerBookingView;
	isFreeCancellation: boolean;
}

export const FailedStatusBlock: FC<IProps> = ({
	bookedItinerary,
	isFreeCancellation,
}) => {
	const { t } = useTranslation(['tickets']);

	const isPaymentFailed =
		bookedItinerary.payment.paymentState === PaymentStateEnum.FAILED;

	const isCancelledByDistributor =
		bookedItinerary.bookingStatus === BookingStateEnum.CANCELLED_BY_DISTRIBUTOR;
	const isCancelledByPassenger =
		bookedItinerary.bookingStatus === BookingStateEnum.CANCELLED_BY_PASSENGER;

	const cancellationReasonText = getCancellationReasonText(
		t,
		bookedItinerary.cancellationReason
	);

	if (!isPaymentFailed || isFreeCancellation) return null;

	if (isCancelledByPassenger) {
		return (
			<Message variant="error" title={t('title.cancelled_by_passenger')}>
				{t('text.cancelled_by_passenger')}
			</Message>
		);
	}

	if (isPaymentFailed) {
		return (
			<Message variant="error" title={t('title.payment_failed')}>
				{t('text.payment_failed')}
			</Message>
		);
	}

	if (isCancelledByDistributor) {
		return (
			<Message variant="error" title={t('title.cancelled_by_distributor')}>
				{t('text.cancelled_by_distributor', {
					reason: cancellationReasonText,
					context: cancellationReasonText && 'reason',
				})}
			</Message>
		);
	}

	return (
		<Message variant="error" title={t('title.cancelled_by_driver')}>
			{t('text.cancelled_by_driver')}
		</Message>
	);
};

function getCancellationReasonText(
	t: TFunction,
	cancellationReason?: CancellationReasonDispatcherBookingEnum | null
) {
	if (!cancellationReason) {
		return null;
	}

	return t(`cancellation_reasons.${cancellationReason}`);
}
